import React, { useCallback, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { ExternalStreamlitApp } from 'libs/end-to-end/src/external-streamlit-apps';
import { ExternalStreamlitApps } from 'libs/end-to-end/src/external-streamlit-apps';
import { CreateExternalStreamlitAppRequest } from 'libs/end-to-end/src/external-streamlit-apps';
import { UpdateExternalStreamlitAppRequest } from 'libs/end-to-end/src/external-streamlit-apps';
import { OrganizationExternalStreamlitAppEntitlement } from 'libs/end-to-end/src/external-streamlit-apps';
import { OrganizationExternalStreamlitAppEntitlements } from 'libs/end-to-end/src/external-streamlit-apps';
import { GrantOrganizationStreamlitAppAccessRequest } from 'libs/end-to-end/src/external-streamlit-apps';
import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import { CustomVizId } from 'libs/end-to-end/src/custom-viz/CustomVizId';
import { CustomVizIds } from 'libs/end-to-end/src/custom-viz/CustomVizId';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

/**
 * ExternalStreamlitAppsPage component
 *
 * This component renders the Streamlit Apps management page, allowing users to view, create, edit, and delete streamlit apps.
 * It also allows granting and revoking organization access to streamlit apps.
 */
export const ExternalStreamlitAppsPage = () => {
  const { userAuthenticatedApiCall } = useUser();
  const language = useLanguage();

  // State for managing the create/edit app modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGrantAccessModalOpen, setIsGrantAccessModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<ExternalStreamlitApp | null>(
    null,
  );
  const [selectedAppForAccess, setSelectedAppForAccess] =
    useState<ExternalStreamlitApp | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isGranting, setIsGranting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [streamlitSlug, setStreamlitSlug] = useState('');
  const [prototypeCustomVizId, setPrototypeCustomVizId] =
    useState<CustomVizId | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');

  // Error state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Local state for apps (for useCrudOperations)
  const [localApps, setLocalApps] = useState<ExternalStreamlitApp[]>([]);
  const [localEntitlements, setLocalEntitlements] = useState<
    OrganizationExternalStreamlitAppEntitlement[]
  >([]);

  /**
   * Callback to get the URL for fetching streamlit apps
   */
  const getAppsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/external-streamlit-apps`;
  }, []);

  /**
   * Hook for fetching and managing streamlit apps data
   */
  const appsReadPattern = useSimpleReadPattern({
    getUrlFn: getAppsUrlFn,
    canRead: true,
    responseSchema: ExternalStreamlitApps,
  });

  /**
   * Callback to get the URL for fetching organizations
   */
  const getOrganizationsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations`;
  }, []);

  /**
   * Hook for fetching and managing organizations data
   */
  const organizationsReadPattern = useSimpleReadPattern({
    getUrlFn: getOrganizationsUrlFn,
    canRead: true,
    responseSchema: Organizations,
  });

  /**
   * Update local apps when API data changes
   */
  React.useEffect(() => {
    if (appsReadPattern.status === 'done' && 'entity' in appsReadPattern) {
      setLocalApps(appsReadPattern.entity);
    }
  }, [appsReadPattern.status, appsReadPattern]);

  /**
   * CRUD operations for local apps
   */
  const crudOps = useCrudOperations({
    items: localApps,
    setItems: setLocalApps,
  });

  /**
   * Function to refresh the apps data
   */
  const refreshApps = useCallback(() => {
    if (
      appsReadPattern.status === 'done' ||
      appsReadPattern.status === 'error'
    ) {
      appsReadPattern.reloadEntity();
    }
  }, [appsReadPattern]);

  /**
   * Function to refresh entitlements for a specific app
   */
  const refreshEntitlements = useCallback(
    async (appId: string) => {
      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationExternalStreamlitAppEntitlements,
          url: `${getApiBaseUrl()}/external-streamlit-apps/${appId}/organizations`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (result && result.resultType === 'ok') {
          setLocalEntitlements(result.value);
        }
      } catch (error) {
        // Silently fail - user will see stale data but won't break the UI
      }
    },
    [userAuthenticatedApiCall],
  );

  /**
   * Function to reset form state
   */
  const resetForm = useCallback(() => {
    setName('');
    setStreamlitSlug('');
    setPrototypeCustomVizId(null);
    setSelectedOrganizationId('');
    setEditingApp(null);
    setSelectedAppForAccess(null);
  }, []);

  /**
   * Function to open create modal
   */
  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  /**
   * Function to open edit modal
   */
  const openEditModal = useCallback((app: ExternalStreamlitApp) => {
    setEditingApp(app);
    setName(app.name);
    setStreamlitSlug(app.streamlitSlug);
    setPrototypeCustomVizId(app.prototypeCustomVizId);
    setIsEditModalOpen(true);
  }, []);

  /**
   * Function to open grant access modal
   */
  const openGrantAccessModal = useCallback(
    async (app: ExternalStreamlitApp) => {
      setSelectedAppForAccess(app);
      setSelectedOrganizationId('');
      setIsGrantAccessModalOpen(true);
      setLocalEntitlements([]);
      await refreshEntitlements(app.id);
    },
    [refreshEntitlements],
  );

  /**
   * Function to close modals
   */
  const closeModals = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsGrantAccessModalOpen(false);
    resetForm();
  }, [resetForm]);

  /**
   * Async callback for creating an app
   */
  const createApp = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive()) return;

      setIsCreating(true);
      setErrorMessage(null);

      try {
        const request: CreateExternalStreamlitAppRequest = {
          name,
          streamlitSlug,
          prototypeCustomVizId,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: ExternalStreamlitApp,
          url: `${getApiBaseUrl()}/external-streamlit-apps`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to create streamlit app');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to create streamlit app',
          );
        }

        // Add to local state immediately for better UX
        crudOps.onAdd(result.value);
        refreshApps();
        closeModals();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to create streamlit app',
        );
      } finally {
        setIsCreating(false);
      }
    },
    [
      name,
      streamlitSlug,
      prototypeCustomVizId,
      userAuthenticatedApiCall,
      refreshApps,
      closeModals,
      crudOps,
    ],
  );

  /**
   * Async callback for updating an app
   */
  const updateApp = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !editingApp) return;

      setIsUpdating(true);
      setErrorMessage(null);

      try {
        const request: UpdateExternalStreamlitAppRequest = {
          name,
          streamlitSlug,
          prototypeCustomVizId,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: ExternalStreamlitApp,
          url: `${getApiBaseUrl()}/external-streamlit-apps/${editingApp.id}`,
          requestOptions: {
            method: 'PUT',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to update streamlit app');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to update streamlit app',
          );
        }

        // Update local state immediately for better UX
        const index = localApps.findIndex((a) => a.id === editingApp.id);
        if (index !== -1) {
          crudOps.onEdit(index, result.value);
        }
        refreshApps();
        closeModals();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to update streamlit app',
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [
      editingApp,
      name,
      streamlitSlug,
      prototypeCustomVizId,
      userAuthenticatedApiCall,
      refreshApps,
      closeModals,
      localApps,
      crudOps,
    ],
  );

  /**
   * Async callback for deleting an app
   */
  const deleteApp = useAsyncCallback(
    async (isAlive, app: ExternalStreamlitApp) => {
      if (!isAlive()) return;

      if (!confirm(`Are you sure you want to delete "${app.name}"?`)) {
        return;
      }

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/external-streamlit-apps/${app.id}`,
          requestOptions: {
            method: 'DELETE',
          },
        });

        if (!result) {
          throw new Error('Failed to delete streamlit app');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to delete streamlit app',
          );
        }

        refreshApps();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to delete streamlit app',
        );
      }
    },
    [userAuthenticatedApiCall, refreshApps],
  );

  /**
   * Async callback for granting access
   */
  const grantAccess = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !selectedAppForAccess) return;

      setIsGranting(true);
      setErrorMessage(null);

      try {
        const request: GrantOrganizationStreamlitAppAccessRequest = {
          organizationId: selectedOrganizationId,
          streamlitAppId: selectedAppForAccess.id,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationExternalStreamlitAppEntitlement,
          url: `${getApiBaseUrl()}/external-streamlit-apps/grant-access`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to grant access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to grant access',
          );
        }

        await refreshEntitlements(selectedAppForAccess.id);
        setSelectedOrganizationId('');
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to grant access',
        );
      } finally {
        setIsGranting(false);
      }
    },
    [
      selectedAppForAccess,
      selectedOrganizationId,
      userAuthenticatedApiCall,
      refreshEntitlements,
    ],
  );

  /**
   * Async callback for revoking access
   */
  const revokeAccess = useAsyncCallback(
    async (
      isAlive,
      entitlement: OrganizationExternalStreamlitAppEntitlement,
    ) => {
      if (!isAlive() || !selectedAppForAccess) return;

      if (!confirm('Are you sure you want to revoke access?')) {
        return;
      }

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/external-streamlit-apps/revoke-access?organizationId=${entitlement.organizationId}&streamlitAppId=${entitlement.streamlitAppId}`,
          requestOptions: {
            method: 'POST',
          },
        });

        if (!result) {
          throw new Error('Failed to revoke access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to revoke access',
          );
        }

        await refreshEntitlements(selectedAppForAccess.id);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to revoke access',
        );
      }
    },
    [selectedAppForAccess, userAuthenticatedApiCall, refreshEntitlements],
  );

  /**
   * Get organization name by ID
   */
  const getOrganizationName = useMemo(() => {
    if (
      organizationsReadPattern.status !== 'done' ||
      !('entity' in organizationsReadPattern)
    ) {
      return (id: string) => id;
    }

    const orgs = organizationsReadPattern.entity.organizations;
    return (id: string) => {
      const org = orgs.find((o) => o.id === id);
      return org?.name || id;
    };
  }, [organizationsReadPattern]);

  /**
   * Table column definitions
   */
  const columnDefinitions = useMemo(
    () => [
      {
        id: 'name',
        header: () => <div>Name</div>,
        cell: ({ item }: { item: ExternalStreamlitApp }) => (
          <div className="font-medium">{item.name}</div>
        ),
      },
      {
        id: 'slug',
        header: () => <div>Streamlit Slug</div>,
        cell: ({ item }: { item: ExternalStreamlitApp }) => (
          <div className="text-gray-600">{item.streamlitSlug}</div>
        ),
      },
      {
        id: 'prototypeCustomVizId',
        header: () => <div>Prototype Custom Viz</div>,
        cell: ({ item }: { item: ExternalStreamlitApp }) => (
          <div className="text-gray-600">
            {item.prototypeCustomVizId
              ? language.customVizDisplayName({
                  customVizId: item.prototypeCustomVizId,
                })
              : 'None'}
          </div>
        ),
      },
      {
        id: 'created',
        header: () => <div>Created</div>,
        cell: ({ item }: { item: ExternalStreamlitApp }) => (
          <div className="text-sm text-gray-500">
            {new Date(item.createdAt).toLocaleDateString()}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({ item }: { item: ExternalStreamlitApp }) => (
          <div className="flex gap-2">
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => openEditModal(item),
              }}
              text="Edit"
              size="small"
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => openGrantAccessModal(item),
              }}
              text="Manage Access"
              size="small"
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => deleteApp(item),
              }}
              text="Delete"
              size="small"
              variant="outline"
            />
          </div>
        ),
      },
    ],
    [openEditModal, openGrantAccessModal, deleteApp],
  );

  /**
   * Entitlements table column definitions
   */
  const entitlementsColumnDefinitions = useMemo(
    () => [
      {
        id: 'organization',
        header: () => <div>Organization</div>,
        cell: ({
          item,
        }: {
          item: OrganizationExternalStreamlitAppEntitlement;
        }) => <div>{getOrganizationName(item.organizationId)}</div>,
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({
          item,
        }: {
          item: OrganizationExternalStreamlitAppEntitlement;
        }) => (
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => revokeAccess(item),
            }}
            text="Revoke Access"
            size="small"
            variant="outline"
          />
        ),
      },
    ],
    [getOrganizationName, revokeAccess],
  );

  return (
    <AdminLayout currentPage={'ExternalStreamlitApps'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Streamlit Apps', to: '/external-streamlit-apps' },
        ]}
      />
      <p>You can manage external Streamlit applications from this screen.</p>

      <S2Typography variant="headline-4" translatedContent="Streamlit Apps" />
      <div className="flex flex-col items-start">
        <Button
          behavior={{
            type: 'onClick',
            onClick: openCreateModal,
          }}
          text="New Streamlit App"
        />
      </div>

      {errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {errorMessage}
        </div>
      )}

      <Table
        items={localApps}
        columnDefinitions={columnDefinitions}
        isLoading={appsReadPattern.status === 'loading'}
        empty={
          <div className="text-center py-8 text-gray-500">
            No streamlit apps found
          </div>
        }
      />

      {/* Create/Edit Modal */}
      <Transition
        appear
        show={isCreateModalOpen || isEditModalOpen}
        as={Fragment}
      >
        <Dialog as="div" className="relative z-10" onClose={closeModals}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isCreateModalOpen
                      ? 'Create Streamlit App'
                      : 'Edit Streamlit App'}
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <TextInput
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        placeholder="Enter app name"
                        className="w-full mt-1"
                        type="text"
                        data-qa="appNameInput"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Streamlit Slug
                      </label>
                      <TextInput
                        value={streamlitSlug}
                        onChange={(e) =>
                          setStreamlitSlug(e.currentTarget.value)
                        }
                        placeholder="Enter streamlit slug"
                        className="w-full mt-1"
                        type="text"
                        data-qa="streamlitSlugInput"
                      />
                    </div>

                    <div>
                      <SelectBoxWithSearch
                        label={'Prototype Custom Viz (optional)'}
                        selected={prototypeCustomVizId ?? 'none'}
                        setSelected={(newPrototypeCustomVizIdOrNone) => {
                          setPrototypeCustomVizId(
                            newPrototypeCustomVizIdOrNone === 'none'
                              ? null
                              : newPrototypeCustomVizIdOrNone,
                          );
                        }}
                        type={'single'}
                        list={[
                          {
                            optionLabel: 'None',
                            optionValue: 'none' as const,
                          },
                          ...CustomVizIds.map((customVizId) => {
                            return {
                              optionLabel: language.customVizDisplayName({
                                customVizId,
                              }),
                              optionValue: customVizId,
                            };
                          }),
                        ]}
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: closeModals,
                      }}
                      text="Cancel"
                      variant="outline"
                    />
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: isCreateModalOpen ? createApp : updateApp,
                      }}
                      text={isCreateModalOpen ? 'Create' : 'Update'}
                      disabled={
                        isCreating || isUpdating || !name || !streamlitSlug
                      }
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Grant Access Modal */}
      <Transition appear show={isGrantAccessModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModals}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Manage Access: {selectedAppForAccess?.name}
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Grant Access to Organization
                      </label>
                      {organizationsReadPattern.status === 'done' &&
                        'entity' in organizationsReadPattern && (
                          <select
                            value={selectedOrganizationId}
                            onChange={(e) =>
                              setSelectedOrganizationId(e.target.value)
                            }
                            className="form-select mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                          >
                            <option value="">Select an organization...</option>
                            {organizationsReadPattern.entity.organizations.map(
                              (org: { id: string; name: string }) => (
                                <option key={org.id} value={org.id}>
                                  {org.name}
                                </option>
                              ),
                            )}
                          </select>
                        )}
                      <div className="mt-2">
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: grantAccess,
                          }}
                          text="Grant Access"
                          disabled={isGranting || !selectedOrganizationId}
                          size="small"
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <S2Typography
                        variant="headline-6-alt"
                        translatedContent="Organizations with Access"
                      />
                      <Table
                        items={localEntitlements}
                        columnDefinitions={entitlementsColumnDefinitions}
                        isLoading={false}
                        empty={
                          <div className="text-center py-4 text-gray-500">
                            No organizations have access yet
                          </div>
                        }
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: closeModals,
                      }}
                      text="Close"
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};
